import {useState, useEffect} from 'react';
import {view} from '@sail/ui';
import {WalletSelectList} from 'src/v2/components/walletSelect/walletList/WalletSelectList';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';
import {
  setValue,
  useSelector,
  resetAnyCryptoRefundState,
} from 'src/v2/state/store';
import {useWalletPress} from 'src/v2/components/walletSelect/useWalletPress';
import {getWalletOptionKey} from 'src/v2/components/walletSelect/getWalletOptionKey';
import type {PaymentOption} from 'src/v2/state/types';

interface Props {
  walletOptions: PaymentOption[];
}

const ALLOWED_WALLETS = [
  'Coinbase Wallet',
  'Phantom',
  'MetaMask',
  'trust',
  'okxwallet',
  'rainbow',
  'ledger',
];

// TODO (stellacai): remove preferred placement for Crypto.com at end of Q1 2026
const ALLOWED_WALLETS_WITH_EXCHANGE = [
  'Coinbase Wallet',
  'Phantom',
  'MetaMask',
  'cryptoDotCom',
  'trust',
  'okxwallet',
  'ledger',
];

export const WalletSelectContent = ({walletOptions}: Props) => {
  const exchangeFeatureFlagEnabled = useSelector(
    (state) => state.featureFlags.enable_crypto_payins_pay_from_exchange,
  );
  const directDepositEnabled = useSelector(
    (state) =>
      state.featureFlags.enable_crypto_payins_direct_deposit &&
      state.settlementPartner === 'bridge' &&
      !state.setupFutureUsage,
  );
  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();

  const directDepositSupportedCurrencies = useSelector(
    (state) => state.directDepositSupportedCurrencies,
  );
  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );

  const setupFutureUsage = useSelector((state) => state.setupFutureUsage);
  const exchangePaymentsEnabled =
    !setupFutureUsage && exchangeFeatureFlagEnabled;

  const defaultWallets = exchangePaymentsEnabled
    ? ALLOWED_WALLETS_WITH_EXCHANGE
    : ALLOWED_WALLETS;

  const filteredWalletOptions = walletOptions
    .filter((walletOption) => {
      return defaultWallets.includes(getWalletOptionKey(walletOption));
    })
    .sort((a, b) => {
      const indexA = defaultWallets.indexOf(getWalletOptionKey(a));
      const indexB = defaultWallets.indexOf(getWalletOptionKey(b));

      return indexA - indexB;
    });

  const connectedWallets = useSelector(
    (state) => state.walletConnection.connectedWallets,
  );
  const [contentVisible, setContentVisible] = useState(false);

  const showUseConnectedWalletsButton = connectedWallets.length > 0;
  const showDirectDeposit =
    directDepositEnabled || isAnyCryptoWalletConnectFlow;

  const handleWalletPress = useWalletPress();

  const handleDirectDepositClick = () => {
    if (isAnyCryptoWalletConnectFlow) {
      const networks = directDepositSupportedCurrencies
        .filter((c) => c.assetCode === selectedToken)
        .map((c) => c.currencyNetwork);

      if (networks.length > 1) {
        setValue((state) => {
          state.anyCryptoDeposit.selectedNetwork = null;
          state.screen = 'ANY_CRYPTO_NETWORK_SELECT';
        });
      } else {
        setValue((state) => {
          state.anyCryptoDeposit.selectedNetwork = networks[0] ?? null;
          resetAnyCryptoRefundState(state);
          state.anyCryptoDeposit.quote = {
            depositAddress: null,
            inputAmount: null,
            inputChain: null,
            inputCurrency: null,
          };
          state.screen = 'ANY_CRYPTO_REFUND';
        });
      }
      return;
    }

    setValue((state) => {
      state.screen = 'TOKEN_SELECT';
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setContentVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleConnectedWalletPress = () => {
    setValue((state) => {
      state.screen = 'WALLET_SWITCH';
    });
  };

  return (
    <view.div
      css={{
        stack: 'y',
        gapY: 'space.150',
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 100ms ease-in-out',
      }}
    >
      <WalletSelectList
        walletOptions={walletOptions}
        defaultWalletOptions={filteredWalletOptions.slice(0, 5)}
        onWalletPress={handleWalletPress}
        onDirectDepositPress={
          showDirectDeposit ? handleDirectDepositClick : undefined
        }
        onConnectedWalletPress={
          showUseConnectedWalletsButton ? handleConnectedWalletPress : undefined
        }
      />
    </view.div>
  );
};
